/**
 * Standards Profiles API Client
 *
 * CRUD operations for standards_profiles table (migration 037).
 * Profiles tune standards delivery PRIORITY, not applicability.
 * Foundation standards (priority 10) are always included regardless of profile.
 *
 * Follows: .equilateral-standards/frontend-development/api_client_standards.md
 */

import { Make_Authorized_API_Call, isSuccessResponse, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export for consumers
export type { TimComboAPIResponse };

// ─── Types ───────────────────────────────────────────────────

export interface StandardsProfile {
  profile_id: string;
  name: string;
  description: string | null;
  tech_stack: Record<string, any>;
  priority_overrides: Record<string, number>;
  keyword_boosts: string[];
  category_includes: string[];
  category_deprioritizes: string[];
  status: 'draft' | 'active' | 'archived';
  effectiveness_score: number;
  times_used: number;
  successful_outcomes: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateProfileInput {
  name: string;
  description?: string;
  tech_stack?: Record<string, any>;
  priority_overrides?: Record<string, number>;
  keyword_boosts?: string[];
  category_includes?: string[];
  category_deprioritizes?: string[];
  status?: 'draft' | 'active' | 'archived';
}

export interface UpdateProfileInput {
  name?: string;
  description?: string;
  tech_stack?: Record<string, any>;
  priority_overrides?: Record<string, number>;
  keyword_boosts?: string[];
  category_includes?: string[];
  category_deprioritizes?: string[];
  status?: 'draft' | 'active' | 'archived';
}

export interface ProfilePreview {
  total_standards: number;
  by_category: Record<string, number>;
  foundation_count: number;
  boosted_keywords: string[];
  enhanced_categories: string[];
  deprioritized_categories: string[];
}

// ─── API Functions ───────────────────────────────────────────

/**
 * List all standards profiles
 * GET /api/standards/profilesGet
 */
export async function listProfiles(
  clientId: string,
): Promise<TimComboAPIResponse<StandardsProfile>> {
  return Make_Authorized_API_Call<StandardsProfile>(
    API_ENDPOINTS.STANDARDS_PROFILES.LIST,
    'GET',
    undefined,
    { params: { Client_ID: clientId } }
  );
}

/**
 * Get a single standards profile by ID
 * GET /api/standards/profileGet
 */
export async function getProfile(
  profileId: string,
): Promise<TimComboAPIResponse<StandardsProfile>> {
  return Make_Authorized_API_Call<StandardsProfile>(
    API_ENDPOINTS.STANDARDS_PROFILES.GET,
    'GET',
    undefined,
    { params: { profile_id: profileId } }
  );
}

/**
 * Create a new standards profile
 * POST /api/standards/profileCreate
 */
export async function createProfile(
  data: CreateProfileInput,
): Promise<TimComboAPIResponse<StandardsProfile>> {
  return Make_Authorized_API_Call<StandardsProfile>(
    API_ENDPOINTS.STANDARDS_PROFILES.CREATE,
    'POST',
    data
  );
}

/**
 * Update an existing standards profile
 * PUT /api/standards/profileUpdate
 */
export async function updateProfile(
  profileId: string,
  data: UpdateProfileInput,
): Promise<TimComboAPIResponse<StandardsProfile>> {
  return Make_Authorized_API_Call<StandardsProfile>(
    API_ENDPOINTS.STANDARDS_PROFILES.UPDATE,
    'PUT',
    { profile_id: profileId, ...data }
  );
}

/**
 * Delete a standards profile
 * DELETE /api/standards/profileDelete
 */
export async function deleteProfile(
  profileId: string,
): Promise<TimComboAPIResponse<void>> {
  return Make_Authorized_API_Call<void>(
    API_ENDPOINTS.STANDARDS_PROFILES.DELETE,
    'DELETE',
    { profile_id: profileId }
  );
}

/**
 * Preview what standards would be delivered with a given profile
 * GET /api/standards/profilePreview
 */
export async function previewProfile(
  profileId: string,
): Promise<TimComboAPIResponse<ProfilePreview>> {
  return Make_Authorized_API_Call<ProfilePreview>(
    API_ENDPOINTS.STANDARDS_PROFILES.PREVIEW,
    'GET',
    undefined,
    { params: { profile_id: profileId } }
  );
}

// ─── Formatting Utilities ────────────────────────────────────

export function getStatusBadgeColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'draft':
      return 'bg-yellow-100 text-yellow-800';
    case 'archived':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

export function formatEffectivenessScore(score: number): string {
  return `${(score * 100).toFixed(1)}%`;
}

export function formatSuccessRate(timesUsed: number, successfulOutcomes: number): string {
  if (timesUsed === 0) return 'No data';
  return `${((successfulOutcomes / timesUsed) * 100).toFixed(1)}%`;
}

/** All available standard categories for the UI */
export const STANDARD_CATEGORIES = [
  'serverless-saas-aws',
  'multi-agent-orchestration',
  'frontend-development',
  'backend',
  'database',
  'database-patterns',
  'compliance-security',
  'security',
  'real-time-systems',
  'cost-optimization',
  'testing',
  'frontend',
] as const;
